/**
 * Density conversion helpers for the proof. grammar (Track 2.A).
 *
 * Implements §4.5 and §6.6 of extensibility-design.md.
 *
 * Provides:
 * - A registry of density entries (default + user overrides + inline hints).
 * - A lookup function that resolves density for an ingredient name.
 * - Precedence: inline hint > user override > bundle default > shipped default.
 *
 * Pure TS — no DOM, no Node built-ins.
 */

import defaultsJson from "../densities/defaults.json" with { type: "json" };

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DensityEntry {
  /** Density in g/ml. */
  density: number;
  /** Human-readable source description. */
  source?: string;
}

export interface DensityRegistry {
  /** Lookup density by ingredient key. Returns null if not found. */
  lookup(ingredientKey: string): number | null;
  /** Merge user overrides (from .cookrc.yml). Returns a new registry. */
  withOverrides(overrides: Record<string, number>): DensityRegistry;
  /** Return a registry with an inline hint added for one ingredient. */
  withInlineHint(ingredientKey: string, density: number): DensityRegistry;
}

// ---------------------------------------------------------------------------
// Default entries (from defaults.json)
// ---------------------------------------------------------------------------

const DEFAULT_ENTRIES: ReadonlyMap<string, number> = new Map(
  Object.entries((defaultsJson as { entries: Record<string, { density: number }> }).entries).map(
    ([k, v]) => [k.toLowerCase(), v.density],
  ),
);

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

class DensityRegistryImpl implements DensityRegistry {
  private readonly base: ReadonlyMap<string, number>;
  private readonly overrides: ReadonlyMap<string, number>;
  private readonly inlineHints: ReadonlyMap<string, number>;

  constructor(
    base: ReadonlyMap<string, number>,
    overrides: ReadonlyMap<string, number>,
    inlineHints: ReadonlyMap<string, number>,
  ) {
    this.base = base;
    this.overrides = overrides;
    this.inlineHints = inlineHints;
  }

  lookup(ingredientKey: string): number | null {
    const key = ingredientKey.toLowerCase().trim();
    // Inline hint wins first
    const hint = this.inlineHints.get(key);
    if (hint !== undefined) return hint;
    // User override next
    const override = this.overrides.get(key);
    if (override !== undefined) return override;
    // Shipped default last
    const base = this.base.get(key);
    if (base !== undefined) return base;
    // Also try a partial match (e.g. "flour" matches "flour (AP)")
    for (const [k, v] of this.base) {
      if (k.startsWith(key) || key.startsWith(k.split(" ")[0] ?? "")) {
        return v;
      }
    }
    return null;
  }

  withOverrides(overrides: Record<string, number>): DensityRegistry {
    const overrideMap = new Map<string, number>(
      Object.entries(overrides).map(([k, v]) => [k.toLowerCase().trim(), v]),
    );
    return new DensityRegistryImpl(this.base, overrideMap, this.inlineHints);
  }

  withInlineHint(ingredientKey: string, density: number): DensityRegistry {
    const newHints = new Map(this.inlineHints);
    newHints.set(ingredientKey.toLowerCase().trim(), density);
    return new DensityRegistryImpl(this.base, this.overrides, newHints);
  }
}

// ---------------------------------------------------------------------------
// Default registry (singleton — shipped defaults, no user overrides)
// ---------------------------------------------------------------------------

export const DEFAULT_DENSITY_REGISTRY: DensityRegistry = new DensityRegistryImpl(
  DEFAULT_ENTRIES,
  new Map(),
  new Map(),
);

/**
 * Create a new density registry with user overrides merged in.
 * Accepts the `densities` map from a parsed `.cookrc.yml`.
 *
 * Precedence: inline hint > user override > shipped default.
 */
export function createDensityRegistry(userOverrides?: Record<string, number>): DensityRegistry {
  return userOverrides !== undefined
    ? DEFAULT_DENSITY_REGISTRY.withOverrides(userOverrides)
    : DEFAULT_DENSITY_REGISTRY;
}
